/*
 * Copyright Mimic Networks, Inc. 2024.
 */

import { Grid } from 'antd';

import { ConfigRevision, NodeConfig } from '@/client';
import { AvatarAndName } from '@/components/AvatarAndName';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Card } from '@/primitives/Card';
import { Col } from '@/primitives/Col';
import { OsIcon } from '@/primitives/Icons';
import { Link } from '@/primitives/Link';
import { Row } from '@/primitives/Row';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';
import { VerticalDivider } from '@/primitives/VerticalDivider';

export type ConfigureNodesJobWizardReviewJobConfigProps = {
  tenantID: string;
  selectedConfig: NodeConfig | undefined;
  selectedRevision: ConfigRevision | undefined;
};

const { useBreakpoint } = Grid;

export function ConfigureNodesJobWizardReviewJobConfig({
  tenantID,
  selectedConfig,
  selectedRevision,
}: ConfigureNodesJobWizardReviewJobConfigProps) {
  const { t } = useMimicTranslation('jobs');
  const screens = useBreakpoint();
  const paddingBottom = screens.xl ? undefined : '8px';
  const containerPadding = screens.sm ? 'md' : 'sm';

  if (!selectedConfig || !selectedRevision) {
    return null;
  }

  return (
    <Card padding={containerPadding} gap="sm">
      <Title level={3} style={{ fontSize: '17px', fontWeight: '700' }}>
        {t('targetConfig')}
      </Title>
      <div style={{ display: 'flex', width: '100%' }}>
        <Row>
          <Col flex="auto">
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: '8px' }}>
              <Link to={`/tenants/${tenantID}/node-configs/${selectedConfig.id}`}>
                <Text mono>{selectedConfig.name}</Text>
              </Link>
              <Row gutter={[16, 8]} style={{ paddingBottom }}>
                <Col>
                  <Text size="sm" type="subtle" style={{ textTransform: 'capitalize' }}>
                    <OsIcon name={selectedConfig.os} /> {selectedConfig.os}
                  </Text>
                </Col>
                {screens.sm && <VerticalDivider />}
                <Col>
                  <Text mono copyable type="subtle" size="sm">
                    {selectedConfig.id}
                  </Text>
                </Col>
              </Row>
            </div>
          </Col>
          <Col flex="auto">
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: '8px' }}>
              <Link
                to={`/tenants/${tenantID}/node-configs/${selectedConfig.id}/revisions/${selectedRevision.revisionNumber}`}
              >
                <Text data-testid="revision-title">
                  {t('revision')} #{selectedRevision.revisionNumber}: {selectedRevision.description}
                </Text>
              </Link>
              <Row gutter={[16, 8]}>
                <Col>
                  <AvatarAndName size="sm" userInfo={selectedRevision.createdBy} />
                </Col>
                {screens.sm && <VerticalDivider />}
                <Col>
                  <Text mono copyable type="subtle" size="sm">
                    {selectedRevision.id}
                  </Text>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </div>
    </Card>
  );
}
